import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { App } from 'antd';
import customerCreditNoteService, {
  CustomerCreditNote,
  CreateCustomerCreditNoteDTO,
  UpdateCustomerCreditNoteDTO,
} from '../services/customerCreditNote.service';
import { getFinanceApiErrorMessage } from '../utils/financeApiError';

export const useCustomerCreditNotes = (filters?: {
  customer_id?: string;
  status?: string;
  from_date?: string;
  to_date?: string;
}): UseQueryResult<CustomerCreditNote[], Error> => {
  return useQuery({
    queryKey: ['customer-credit-notes', filters],
    queryFn: () => customerCreditNoteService.list(filters),
  });
};

export const useCustomerCreditNote = (id: string | null): UseQueryResult<CustomerCreditNote, Error> => {
  return useQuery({
    queryKey: ['customer-credit-notes', id],
    queryFn: () => customerCreditNoteService.getById(id!),
    enabled: !!id,
  });
};

export const useCreateCustomerCreditNote = (): UseMutationResult<
  CustomerCreditNote,
  Error,
  CreateCustomerCreditNoteDTO
> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (dto) => customerCreditNoteService.create(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-credit-notes'] });
      message.success('Credit note created');
    },
    onError: (error: Error) => {
      const apiMessage = (error as any)?.response?.data?.message;
      message.error(apiMessage ?? error?.message ?? 'Failed to create credit note');
    },
  });
};

export const useUpdateCustomerCreditNote = (): UseMutationResult<
  CustomerCreditNote,
  Error,
  { id: string; dto: UpdateCustomerCreditNoteDTO }
> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: ({ id, dto }) => customerCreditNoteService.update(id, dto),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['customer-credit-notes'] });
      queryClient.invalidateQueries({ queryKey: ['customer-credit-notes', id] });
      message.success('Credit note updated');
    },
    onError: (error: Error) => {
      const apiMessage = (error as any)?.response?.data?.message;
      message.error(apiMessage ?? error?.message ?? 'Failed to update credit note');
    },
  });
};

export const usePostCustomerCreditNote = (): UseMutationResult<CustomerCreditNote, Error, string> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (id) => customerCreditNoteService.post(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['customer-credit-notes'] });
      queryClient.invalidateQueries({ queryKey: ['customer-credit-notes', id] });
      message.success('Credit note posted');
    },
    onError: (error: Error) => {
      const text = getFinanceApiErrorMessage(error, 'Failed to post credit note');
      if (/journal|GL|account|Chart of accounts|balance|Draft/i.test(text)) {
        message.open({ type: 'warning', content: text, duration: 10 });
      } else {
        message.error(text, 6);
      }
    },
  });
};

export const useCancelCustomerCreditNote = (): UseMutationResult<CustomerCreditNote, Error, string> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (id) => customerCreditNoteService.cancel(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['customer-credit-notes'] });
      queryClient.invalidateQueries({ queryKey: ['customer-credit-notes', id] });
      message.success('Credit note cancelled');
    },
    onError: (error: Error) => {
      message.error(getFinanceApiErrorMessage(error, 'Failed to cancel credit note'), 6);
    },
  });
};
